
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import AgentCard from "@/components/agent-card";
import FadeInWhenVisible from "./FadeInWhenVisible";
import { AgentDiscoveryService } from "@/services/statistics/agent-discovery-service";

const FeaturedAgentsSection: React.FC = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedAgents = async () => {
      try {
        setLoading(true);
        const realFeaturedAgents = await AgentDiscoveryService.getFeaturedAgents(4);
        setAgents(realFeaturedAgents);
      } catch (error) {
        console.error("Failed to fetch featured agents:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedAgents();
  }, []);

  return (
    <section className="py-20 bg-gradient-to-b from-purple-900 to-white dark:to-gray-950">
      <div className="container mx-auto px-4">
        <FadeInWhenVisible>
          <div className="text-center mb-12">
            <Badge className="mb-2">Featured Agents</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white dark:text-white">
              {agents.length > 0 ? "Trending AI Agents" : "Discover AI Agents"}
            </h2>
            <p className="text-lg text-purple-100 dark:text-gray-300 max-w-2xl mx-auto">
              {agents.length > 0
                ? "Discover the most popular and powerful AI agents that are transforming how people work and create"
                : "Be the first to publish an AI agent and start the revolution"}
            </p>
          </div>
        </FadeInWhenVisible>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {loading ? (
            Array(4).fill(0).map((_, index) => (
              <div key={index} className="bg-gray-200 dark:bg-gray-800 animate-pulse rounded-lg h-80"></div>
            ))
          ) : agents.length > 0 ? (
            agents.map((agent, index) => (
              <FadeInWhenVisible key={agent.id} delay={index * 0.1}>
                <AgentCard agent={agent} />
              </FadeInWhenVisible>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No featured agents available yet.</p>
              <Button asChild variant="outline" size="lg" className="mt-4">
                <Link to="/create-agent">Create Your First Agent</Link>
              </Button>
            </div>
          )}
        </div>

        {agents.length > 0 && (
          <div className="text-center mt-12">
            <Button asChild variant="outline" size="lg" className="group">
              <Link to="/explore/featured" className="flex items-center">
                View All Featured Agents
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedAgentsSection;
